import AbstractGenerator from '../../../misc/generator';
import AbstractObject from '../../../misc/object';

import Ground from '../objects/ground';
import Panel from '../../../objects/panel';
import Barrier from '../objects/barrier';
import Arrow from '../../../objects/arrow';
import Line from '../../../objects/line';
import { HTMLLabel, HTMLEditableLabel } from '../../../objects/html-label';
import { unitTransform } from '@services/helper.service';
import { defaultUnits } from '@config';

import Scene from "@scene";
import { LabelsPadding } from '@scene/objects/ground';
import * as THREE from 'three';

import { generateScrewGroup } from './helper';
import { ScrewGroup } from '@scene/objects/screw';
import Cladding, { CladdingTypes } from './cladding';

export default class Pedestal extends AbstractGenerator {

  constructor(options, dispacher) {
    super(options);

    this.dispatchOptionUpdate = dispacher;

    this.options.ground.type = Ground.types.PEDESTAL;
    this.options.barrier.type = Barrier.types.STAIRS;
    this.options.barrier.tilt = 0;
    if (this.options.screw.count.n === 3) {
      this.options.panel.circleCorrection = 90;
    }

    this.panels = new AbstractObject();
  }

  init3DObject() {
    // Calculate ground height
    this.options.ground.pedestal.width = this.options.barrier.dpot * this.options.barrier.stayCount;

    // Create ground object
    this.ground = new Ground(this.options.ground);
    this.ground.onOptionUpdate(this.dispatchOptionUpdate);
    this.ground.mesh.rotation.y = Math.PI;
    this.ground.mesh.rotation.x = Math.PI / 2;
    this.layer3D.addObject(this.ground);

    const GROUND_TOP = this.ground.position.y + this.ground.size.depth;
    const PADDING_TOP = this.options.panel.type === Panel.types.BEND ? this.options.panel.depth : -this.DPP;
    const VERTICAL_PANELS_POS_Y = GROUND_TOP + PADDING_TOP 
      - (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2);

    // Create barrier object
    if (this.options.panel.position === Panel.positions.VERTICAL_X || 
      this.options.panel.position === Panel.positions.VERTICAL_X_INVERT) 
    {
      // the HGC dimension must be limited at the level of concrete, so add the offset between bottom barrier and level of concrete
      this.options.barrier.baseOffset = -(VERTICAL_PANELS_POS_Y + this.options.beam.shift.y - GROUND_TOP);
    }
    this.barrier = new Barrier(this.options.barrier);
    this.barrier.mesh.rotation.y = Math.PI / 2;
    this.layer3D.addObject(this.barrier);

    this.barrier.staysPositions.forEach((item, index) => {
      // const panel = new Panel({
      //   ...this.options.panel//,
      //   //screw: this.options.screw
      // });
      const panel = new Panel(this.options.panel);
      panel.onOptionUpdate(this.dispatchOptionUpdate);
      panel.mesh.position.set(item.z, item.y, -item.x);
      panel.mesh.rotation.y = Math.PI / 2;

      if (index === 1) {
        this.middlePanel = panel;
        this.screwGroup = generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
      } else {
        generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
      }
      this.panels.object.addObject(panel);
    });

    let barrierXPosition;
    let barrierYPosition;

    switch (this.options.panel.position) {
      case Panel.positions.HORIZONTAL_Y: {
        const PEDESTAL_EDGE = this.ground.size.pedestal.height / 2 - this.ground.size.ground.padding / 2;

        this.panels.position.set(
          this.ground.position.x - (PEDESTAL_EDGE - 
            (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2)) + this.DPP,
          this.ground.position.y + this.ground.size.depth + this.options.panel.depth / 2,
          this.ground.position.z
        );

        barrierXPosition = this.panels.position.x;
        barrierYPosition = this.panels.position.y + this.barrier.size.height / 2 + this.middlePanel.size.depth / 2;

        // Create screw labels
        const screwLabels = this.screwGroup.getScrewLabels();
        screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
          htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
          // htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2);
          htmlElem.position.add(this.panels.mesh.position);
          htmlElem.optimalCameraDist = 3000;
          htmlElem.zoomFactor = 0.5;
        });
        this.layer3D.addObject(screwLabels);

        break;
      }

      case Panel.positions.VERTICAL_X: {
        this.panels.position.set(
          this.ground.position.x + this.ground.size.height / 2 + this.options.panel.depth / 2,
          VERTICAL_PANELS_POS_Y,
          this.ground.position.z
        );

        if (this.options.panel.type === Panel.types.BEND) {
          this.panels.mesh.rotation.z = Math.PI;
        } else {
          this.panels.mesh.rotation.z = Math.PI / -2;
        }

        barrierXPosition = this.panels.position.x + this.options.panel.depth / 2 + this.barrier.size.depth / 2;
        barrierYPosition = this.panels.position.y + this.barrier.size.height / 2;

        // Create screw labels
        const screwLabels = this.screwGroup.getScrewLabels();
        screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
          htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
          htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2);
          htmlElem.position.add(this.panels.mesh.position);
          htmlElem.optimalCameraDist = 3000;
          htmlElem.zoomFactor = 0.5;
        });
        this.layer3D.addObject(screwLabels);

        break;
      }

      case Panel.positions.VERTICAL_X_INVERT: {
        this.panels.position.set(
          this.ground.position.x + this.ground.size.ground.width / 2 - this.ground.size.pedestal.height - this.options.panel.depth / 2,
          VERTICAL_PANELS_POS_Y,
          this.ground.position.z
        );

        if (this.options.panel.type === Panel.types.BEND) {
          this.panels.mesh.rotation.z = 0;
        } else {
          this.panels.mesh.rotation.z = Math.PI / -2;
        }

        barrierXPosition = this.panels.position.x - this.options.panel.depth / 2 - this.barrier.size.depth / 2;
        barrierYPosition = this.panels.position.y + this.barrier.size.height / 2;

        // Create screw labels
        const screwLabels = this.screwGroup.getScrewLabels();
        screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
          htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
          htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2);
          htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
          htmlElem.position.add(this.panels.mesh.position);
          htmlElem.optimalCameraDist = 3000;
          htmlElem.zoomFactor = 0.5;
        });
        this.layer3D.addObject(screwLabels);

        break;
      }

      default: {
        break;
      }
    }
    
    this.layer3D.addObject(this.panels);

    this.barrier.position.set(
      barrierXPosition,
      barrierYPosition,
      this.panels.position.z
    );

    this.shiftBarrier();
    this.layer3D.addObject(this.barrier);

    if (this.options.object.dimensions.display || this.options.object.dimensions.renderForReport) {
      // display on the report regardless of the display setting
      // Create dpot label
      const dpot = this.barrier.createDpotLabel(this.dispatchOptionUpdate.bind(this), this.options.object.dimensions);
      if (this.options.panel.position === Panel.positions.VERTICAL_X_INVERT) {
        dpot.arrow.mesh.position.z = this.barrier.options.dpot;
        dpot.htmlElement.position.z *= -1;
      }
      this.layer3D.addObject(dpot.arrow);
      this.layer3D.htmlLayer.add(dpot.htmlElement);

      // Create hgs label
      const hgs = this.barrier.createHgsLabel(this.dispatchOptionUpdate.bind(this), this.options.object.dimensions);
      if (this.options.panel.position === Panel.positions.VERTICAL_X_INVERT) {
        hgs.arrow.rotation.y = Math.PI;
        hgs.arrow.position.x = 2*this.barrier.position.x;
        hgs.htmlElement.position.z *= -1;
      }
      this.layer3D.addMesh(hgs.arrow);
      this.layer3D.htmlLayer.add(hgs.htmlElement);
    }
    if (this.options.object.dimensions.display) {
      this.addLabels();

      // Create href label
      const href = this.ground.createHrefLabel(this.dispatchOptionUpdate.bind(this), this.options.object.dimensions);
      if (this.options.panel.position === Panel.positions.VERTICAL_X_INVERT) {
        href.arrow.mesh.rotation.y = Math.PI;
        href.arrow.mesh.position.x = 2*(-this.ground.finish.position.x - this.ground.size.finish.height / 2);
        href.htmlElement.position.z *= -1;
      }
      this.layer3D.addObject(href.arrow);
      this.layer3D.htmlLayer.add(href.htmlElement); 
      
      // Create pedestal width label
      const pedestalWidth = this.ground.createPedestalWidthLabel(
        this.dispatchOptionUpdate.bind(this), 
        this.options.object.dimensions, 
        this.options.panel.position === Panel.positions.VERTICAL_X_INVERT
      );
      this.layer3D.addMesh(pedestalWidth.arrow);
      this.layer3D.htmlLayer.add(pedestalWidth.htmlElement); 
    }

    // Create cladding between barrier posts
    if (this.options.cladding.type !== CladdingTypes.None) {
      this.claddings = new AbstractObject();
      for (let i = 0; i < this.barrier.staysPositions.length - 1; i++) {
        let cladding = new Cladding({
          type: this.options.type,
          barrier: this.options.barrier,
          beamGeometry: this.barrier.stays[0].beam && this.barrier.stays[0].beam.geometry,
          cladding: this.options.cladding,
          stayHeight: this.barrier.size.stayHeight
        });
        cladding.mesh.position.z += -this.barrier.staysPositions[i].x;
        this.claddings.object.addObject(cladding);
      }

      this.claddings.position.x = this.barrier.position.x;
      this.claddings.position.y = GROUND_TOP;
      if (this.options.panel.position === Panel.positions.HORIZONTAL_Y)
        this.claddings.position.y += this.options.panel.depth;

      this.layer3D.addObject(this.claddings);
    }
  }

  init2DObject() {
    this.layer2D.addObject(this.panels);

    // Create ground object
    this.options.ground.pedestal.width = 400 + this.options.panel.width;

    this.ground = new Ground( { ...this.options.ground, removeFinish: this.options.panel.position === Panel.positions.VERTICAL_X_INVERT});
    this.ground.onOptionUpdate(this.dispatchOptionUpdate);
    this.ground.mesh.rotation.y = Math.PI;
    this.ground.mesh.rotation.x = Math.PI / 2;
    this.layer2D.addObject(this.ground);

    // Create barrier object
    this.options.barrier.stayCount = 1;
    this.options.barrier.disableTop = true;

    this.barrier = new Barrier(this.options.barrier);
    this.barrier.mesh.rotation.y = Math.PI / 2;
    this.layer3D.addObject(this.barrier);

    const panel = new Panel({
      ...this.options.panel//,
      //screw: this.options.screw
    });
    panel.onOptionUpdate(this.dispatchOptionUpdate);
    const panelPosition = this.barrier.staysPositions[0];
    panel.mesh.position.set(panelPosition.z, panelPosition.y, -panelPosition.x);
    this.middlePanel = panel;
    this.panels.object.addObject(panel);

    this.screwGroup = generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);

    let barrierXPosition = this.panels.position.x;
    const panelHeight = this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width : this.options.panel.height;
    let htmlLayerOffset = new THREE.Vector3();
    switch (this.options.panel.position) {
      case Panel.positions.HORIZONTAL_Y: {
        const PEDESTAL_EDGE = this.ground.size.pedestal.height / 2 - this.ground.size.ground.padding / 2;

        this.panels.position.set(
          this.ground.position.x - (PEDESTAL_EDGE - panelHeight / 2) + this.DPP,
          this.ground.position.y + this.ground.size.depth + this.options.panel.depth / 2,
          this.ground.position.z
        );

        htmlLayerOffset.x = this.panels.position.z;
        htmlLayerOffset.z = this.panels.position.x;

        barrierXPosition = this.panels.position.x;

        panel.mesh.rotation.y = Math.PI / 2;
        break;
      }

      case Panel.positions.VERTICAL_X: {
        const GROUND_TOP = this.ground.position.y + this.ground.size.depth;
        const PADDING_TOP = this.options.panel.type === Panel.types.BEND ? this.options.panel.depth : -this.DPP;

        this.panels.position.set(
          this.ground.position.x + this.ground.size.height / 2 + this.options.panel.depth / 2,
          GROUND_TOP + PADDING_TOP - panelHeight / 2,
          this.ground.position.z
        );
        
        htmlLayerOffset.x = this.panels.position.z;
        htmlLayerOffset.z = -this.panels.position.y;

        barrierXPosition = this.panels.position.x + this.options.panel.depth;
        break;
      }

      case Panel.positions.VERTICAL_X_INVERT: {
        const GROUND_TOP = this.ground.position.y + this.ground.size.depth;
        const PADDING_TOP = this.options.panel.type === Panel.types.BEND ? this.options.panel.depth : -this.DPP;

        this.panels.position.set(
          this.ground.position.x + this.ground.size.ground.width / 2 - this.ground.size.pedestal.height - this.options.panel.depth / 2,
          GROUND_TOP + PADDING_TOP - panelHeight/ 2,
          this.ground.position.z
        );

        htmlLayerOffset.x = this.panels.position.z;
        htmlLayerOffset.z = -this.panels.position.y;
        
        barrierXPosition = this.panels.position.x - this.options.panel.depth;
        break;
      }

      default: {
        break;
      }
    }

    const { compressionLine } = panel.getCompressionLine();
    if (compressionLine) {
      compressionLine.mesh.rotation.copy(panel.rotation);
      compressionLine.mesh.position.copy(this.panels.position);
      this.layer2D.addObject(compressionLine);
      let zonesLayer = panel.createZonesLabels();
      if (zonesLayer) {
        zonesLayer.mesh.rotation.copy(panel.rotation);
        zonesLayer.mesh.position.copy(this.panels.position);
        zonesLayer.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
          htmlElem.position.x += htmlLayerOffset.x;
          htmlElem.position.y += htmlLayerOffset.y;
          htmlElem.position.z += htmlLayerOffset.z;
        });
        this.layer2D.addLayer(zonesLayer);
      }
    }

    this.barrier.position.set(
      barrierXPosition,
      this.panels.position.y + this.barrier.size.height / 2,
      this.panels.position.z
    );
    this.shiftBarrier();
    this.layer2D.addObject(this.barrier);
    
    if (this.options.panel.margin && this.options.panel.margin.top) {
      this.panels.mesh.position.y += this.options.panel.margin.top;
    }

    this.addLabels();

    // mask on camera
    this.barrier.mesh.ignoreOnCameraReset = true;
    this.barrier.mesh.traverseVisible(function (child) {
      if (child instanceof THREE.Mesh) {
        child.ignoreOnCameraReset = true;
      }
    });
  }

  addLabels() {
    const layer = this.is3DMode ? this.layer3D : this.layer2D;
    let panelHTMLLabelsCorrection = 0;
    if (this.options.panel.margin && this.options.panel.margin.top) {
      panelHTMLLabelsCorrection += this.options.panel.margin.top;
    }
    let options = null;
    let screwLabels = null;
    if (this.is3DMode) {
      options = {
        is3DMode: true,
        // rotation: new THREE.Euler(Math.PI / 2, 0, 0), //this.layer3D.mesh.rotation,
        showLabels: this.options.object.dimensions.showLabels,
        displayHorizontally: this.options.object.dimensions.displayHorizontally,
        isSafetyBarrier: true
      };

      // Create H1 label
      const h1Label = this.createH1SideViewLabel(options);
      if (this.options.panel.position === Panel.positions.VERTICAL_X_INVERT) {
        h1Label.arrow.rotation.y = 0;
        h1Label.arrow.position.x = -this.ground.pedestal.position.x - this.ground.size.pedestal.height;
        h1Label.htmlElement.position.set(
          -this.ground.pedestal.position.x - this.ground.size.pedestal.height, 
          this.ground.size.ground.depth + this.ground.size.pedestal.depth / 2,
          h1Label.htmlElement.position.x + 20
        );
      } else {
        h1Label.arrow.rotation.y = Math.PI;
        h1Label.arrow.position.x = -this.ground.pedestal.position.x;
        h1Label.htmlElement.position.set(
          -this.ground.pedestal.position.x, 
          this.ground.size.ground.depth + this.ground.size.pedestal.depth / 2,
          -h1Label.htmlElement.position.x - 20
        );
      }
      
      // if (!options.displayHorizontally) {
      //   h1Label.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
      //   let h1HtmlDist = Math.max(25, this.ground.size.pedestal.depth / 2);
      //   h1Label.htmlElement.setDirection(
      //     h1Label.htmlElement.position.clone().setY(h1Label.htmlElement.position.y + h1HtmlDist),
      //     h1Label.htmlElement.position.clone().setY(h1Label.htmlElement.position.y - h1HtmlDist),
      //     false
      //   );
      // }
      layer.addMesh(h1Label.arrow);
      layer.htmlLayer.add(h1Label.htmlElement);

      // Create H2 label
      const h2Label = this.createH2SideViewLabel(options);
      if (this.options.panel.position === Panel.positions.VERTICAL_X_INVERT) {
        h2Label.arrow.rotation.y = 0;
        h2Label.arrow.position.x = -this.ground.pedestal.position.x - this.ground.size.pedestal.height; 
        h2Label.htmlElement.position.set(
          -this.ground.pedestal.position.x - this.ground.size.pedestal.height, 
          this.ground.size.ground.depth / 2, 
          h2Label.htmlElement.position.x + 20
        );
      } else {
        h2Label.arrow.rotation.y = Math.PI;
        h2Label.arrow.position.x = -this.ground.pedestal.position.x; 
        h2Label.htmlElement.position.set(
          -this.ground.pedestal.position.x, 
          this.ground.size.ground.depth / 2, 
          -h2Label.htmlElement.position.x - 20
        );
      }
      // if (!options.displayHorizontally) {
      //   h2Label.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
      //   let h2HtmlDist = Math.max(25, this.ground.size.ground.depth / 2);
      //   h2Label.htmlElement.setDirection(
      //     h2Label.htmlElement.position.clone().setY(h2Label.htmlElement.position.y + h2HtmlDist),
      //     h2Label.htmlElement.position.clone().setY(h2Label.htmlElement.position.y - h2HtmlDist),
      //     false
      //   );
      // }
      layer.addMesh(h2Label.arrow);
      layer.htmlLayer.add(h2Label.htmlElement);
    } else if (this.is2DMode) {
      screwLabels = this.screwGroup.getScrewLabels();
      layer.addObject(screwLabels);
    }
    
    switch (this.options.panel.position) {
      case Panel.positions.HORIZONTAL_Y: {

        // Create screw labels
        if (this.is2DMode) {
          screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
            htmlElem.position.x += this.panels.position.z;
            // htmlElem.position.y += this.panels.position.x;
            htmlElem.position.z += this.panels.position.x;
          });

          const { htmlElement: wHtmlElement } = this.createWLabel();
          layer.htmlLayer.add(wHtmlElement);

          const { htmlElement: h1HtmlElement } = this.createH1Label();
          layer.htmlLayer.add(h1HtmlElement);
        }

        // Create label for Dy
        const dyLabel = this.screwGroup.createDyLabel(null, options);
        if (this.is3DMode) {
          dyLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
          dyLabel.htmlElement.position.set(
            this.panels.position.x + dyLabel.arrow.position.z - this.middlePanel.options.dy.value / 2, 
            this.panels.position.y + dyLabel.arrow.position.x + this.middlePanel.size.depth / 2,
            dyLabel.arrow.position.y + 20
          );
          dyLabel.htmlElement.optimalCameraDist = 3000;
          dyLabel.htmlElement.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            // dyLabel.htmlElement.position.z += 20;
            let dyHtmlDist = Math.max(25, this.middlePanel.options.dy.value / 2);
            dyLabel.htmlElement.setDirection(
              dyLabel.htmlElement.position.clone().setX(dyLabel.htmlElement.position.x + dyHtmlDist),
              dyLabel.htmlElement.position.clone().setX(dyLabel.htmlElement.position.x - dyHtmlDist),
              false
            );
          }
        } else {
          dyLabel.htmlElement.position.set(
            this.panels.position.z - dyLabel.arrow.position.y,
            this.panels.position.y,
            this.panels.position.x + dyLabel.arrow.position.z - this.middlePanel.options.dy.value / 2,
          );
        }
        layer.htmlLayer.add(dyLabel.htmlElement);

        // Create depth label for panel
        let panelDepth = null;
        if (this.is3DMode) {
          panelDepth = this.middlePanel.create3DDepthLabel(options);
          panelDepth.object.rotation.z = Math.PI; // move on the other side to prevent overlap with DPP label
          this.middlePanel.mesh.add(panelDepth.object);
          panelDepth.htmlElement.align = HTMLEditableLabel.types.align.RIGHT;
          panelDepth.htmlElement.position.set(
            this.panels.position.x + panelDepth.htmlElement.position.x,
            this.panels.position.y,
            -panelDepth.htmlElement.position.z,
          );
          panelDepth.htmlElement.optimalCameraDist = 3000;
          panelDepth.htmlElement.zoomFactor = 0.5;
        } else {
          panelDepth = this.middlePanel.createDepthLabel();
          panelDepth.htmlElement.position.set(
            this.panels.position.z - panelDepth.path[1].y,
            this.panels.position.y,
            this.panels.position.x + panelDepth.path[1].z,
          );
        }
        layer.htmlLayer.add(panelDepth.htmlElement);

        // Create width label
        const panelWidth = this.middlePanel.createWidthLabel(options);
        this.middlePanel.object.addMesh(panelWidth.line);
        if (this.is3DMode) {
          panelWidth.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
          panelWidth.htmlElement.position.set(
            this.panels.position.x + panelWidth.htmlElement.position.x,
            this.panels.position.y, 
            this.panels.position.z,
          );
          panelWidth.htmlElement.optimalCameraDist = 3000;
          panelWidth.htmlElement.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            let panelWidthHtmlDist = Math.max(25, this.middlePanel.size.width / 2);
            panelWidth.htmlElement.setDirection(
              panelWidth.htmlElement.position.clone().setZ(panelWidth.htmlElement.position.z + panelWidthHtmlDist),
              panelWidth.htmlElement.position.clone().setZ(panelWidth.htmlElement.position.z - panelWidthHtmlDist),
              false
            );
          }
        } else {
          panelWidth.htmlElement.position.z += this.panels.position.x;
        }
        layer.htmlLayer.add(panelWidth.htmlElement);

        // For not CIRCLE panel type
        if (this.middlePanel.options.type !== Panel.types.CIRCLE) {
          // Create height label
          const panelHeight = this.middlePanel.createHeightLabel(options);
          this.middlePanel.object.addMesh(panelHeight.line);
          if (this.is3DMode) {
            panelHeight.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            panelHeight.htmlElement.position.set(
              this.panels.position.x, 
              this.panels.position.y,
              -panelHeight.htmlElement.position.x - 20
            );
            panelHeight.htmlElement.optimalCameraDist = 3000;
            panelHeight.htmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              // heightHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
              let panelHeightHtmlDist = Math.max(25, this.middlePanel.size.height / 2);
              panelHeight.htmlElement.setDirection(
                panelHeight.htmlElement.position.clone().setX(panelHeight.htmlElement.position.x + panelHeightHtmlDist),
                panelHeight.htmlElement.position.clone().setX(panelHeight.htmlElement.position.x - panelHeightHtmlDist),
                false
              );
            }
          } else {
            panelHeight.htmlElement.position.z += this.panels.position.x;
          }
          layer.htmlLayer.add(panelHeight.htmlElement);
        } else if (this.middlePanel.options.type === Panel.types.CIRCLE) {
          const screwCircle = this.screwGroup.createScrewCircle(options);
          this.middlePanel.object.addMesh(screwCircle.object);
          if (this.is3DMode) {
            screwCircle.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            screwCircle.htmlElement.verticalAlign = HTMLEditableLabel.types.align.CENTER;
            screwCircle.htmlElement.position.set(
              this.panels.position.x + screwCircle.htmlElement.position.x, 
              this.panels.position.y + this.middlePanel.options.depth / 2,
              this.panels.position.z - screwCircle.htmlElement.position.z
            );
            screwCircle.htmlElement.optimalCameraDist = 3000;
            screwCircle.htmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              // circleHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
              screwCircle.htmlElement.setDirection(
                screwCircle.htmlElement.position.clone().setZ(
                  screwCircle.htmlElement.position.z + Math.max(25, this.screwGroup.options.padding.diameter / 2)),
                screwCircle.htmlElement.position.clone().setZ(
                  screwCircle.htmlElement.position.z - Math.max(25, this.screwGroup.options.padding.diameter / 2)),
                false
              );
            }
          } else {
            screwCircle.htmlElement.position.z += this.panels.position.x;
          }
          layer.htmlLayer.add(screwCircle.htmlElement);
        }

        if (this.middlePanel.options.type === Panel.types.SQUARE) {
          if (this.options.screw.count.n > 1) {
            // Create S1 label
            const { object: s1Object, htmlElement: s1HtmlElement } = this.middlePanel.createS1Label(
              this.screwGroup, 
              false, 
              LabelsPadding, 
              this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
              options
            );
            this.middlePanel.object.addMesh(s1Object);
            
            if (this.is3DMode) {
              s1HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
              s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
              s1HtmlElement.position.set(
                this.panels.position.x + s1HtmlElement.position.z + 20, 
                this.panels.position.y + this.middlePanel.size.depth / 2,
                this.panels.position.z
              );
              s1HtmlElement.optimalCameraDist = 3000;
              s1HtmlElement.zoomFactor = 0.5;
              if (!options.displayHorizontally) {
                s1HtmlElement.setDirection(
                  s1HtmlElement.position.clone().setZ(
                    s1HtmlElement.position.z + Math.max(25, this.screwGroup.options.padding.s1 / 2)),
                  s1HtmlElement.position.clone().setZ(
                    s1HtmlElement.position.z - Math.max(25, this.screwGroup.options.padding.s1 / 2)),
                  false
                );
              }
            } else {
              s1HtmlElement.position.z += this.panels.position.x;
            }
            layer.htmlLayer.add(s1HtmlElement);
          }

          if (this.options.screw.count.m > 1 || this.options.screw.formation === ScrewGroup.formation.TRIANGLE) {
            // Create S2 label
            const { object: s2Object, htmlElement: s2HtmlElement } = this.middlePanel.createS2Label(
              this.screwGroup, 
              true, 
              this.middlePanel.size.width / 2 + LabelsPadding, 
              this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
              options
            );
            if (s2HtmlElement && s2Object) {
              if (this.is3DMode) {
                s2HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
                s2HtmlElement.position.set(
                  this.panels.position.x + s2HtmlElement.position.z, 
                  this.panels.position.y + s2HtmlElement.position.y,
                  -s2HtmlElement.position.x + 20
                );
                s2HtmlElement.optimalCameraDist = 3000;
                s2HtmlElement.zoomFactor = 0.5;
                if (!options.displayHorizontally) {
                  // s2HtmlElement.position.z += 20;
                  s2HtmlElement.setDirection(
                    s2HtmlElement.position.clone().setX(
                      s2HtmlElement.position.x + Math.max(25, this.screwGroup.options.padding.s2 / 2)),
                    s2HtmlElement.position.clone().setX(
                        s2HtmlElement.position.x - Math.max(25, this.screwGroup.options.padding.s2 / 2)),
                    false
                  );
                }
            } else {
              s2HtmlElement.position.z += this.panels.position.x;
            }
              this.middlePanel.object.addMesh(s2Object);
              layer.htmlLayer.add(s2HtmlElement);
            }
          }
        }

        // Create Dpp label
        const dppLabel = this.createDppLabel(this.middlePanel, options);
        if (this.is3DMode) {
          dppLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
          dppLabel.htmlElement.position.set(
            this.panels.position.x + dppLabel.arrow.position.z - this.DPP / 2,
            this.panels.position.y,
            this.panels.position.z + dppLabel.arrow.position.y - 20
          );
          dppLabel.htmlElement.optimalCameraDist = 3000;
          dppLabel.htmlElement.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            dppLabel.htmlElement.setDirection(
              dppLabel.htmlElement.position.clone().setX(
                dppLabel.htmlElement.position.x + Math.max(25, this.DPP / 2)),
              dppLabel.htmlElement.position.clone().setX(
                dppLabel.htmlElement.position.x - Math.max(25, this.DPP / 2)),
              false
            );
          }
        } else {
          dppLabel.htmlElement.position.set(
            -this.panels.position.z - dppLabel.arrow.position.y,
            this.panels.position.y,
            this.panels.position.x + dppLabel.arrow.position.z - this.DPP / 2
          );
        }
        layer.htmlLayer.add(dppLabel.htmlElement);

        break;
      }

      case Panel.positions.VERTICAL_X: {
        
        if (this.is2DMode) {
          // Create screw labels
          screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
            htmlElem.position.x += this.panels.position.z;
            // htmlElem.position.y += this.panels.position.x;
            htmlElem.position.z -= this.panels.position.y + (this.options.panel.type === Panel.types.BEND ? 
              this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0);
          });

          // Create H1 label
          const { htmlElement: h1HtmlElement, arrow: h1Arrow } = this.createH1SideViewLabel();
          h1Arrow.rotation.y = Math.PI;
          layer.addMesh(h1Arrow);
          layer.htmlLayer.add(h1HtmlElement);
  
          // Create H2 label
          const { htmlElement: h2HtmlElement, arrow: h2Arrow } = this.createH2SideViewLabel();
          h2Arrow.rotation.y = Math.PI;
          layer.addMesh(h2Arrow);
          layer.htmlLayer.add(h2HtmlElement);

          // Create Href label
          const { htmlElement: hrefHtmlElement } = this.createHref2DDashedLabel();
          layer.htmlLayer.add(hrefHtmlElement);
        }

        // Create label for Dy
        const { htmlElement, arrow } = this.screwGroup.createDyLabel(null, options);
        if (this.is2DMode) {
          htmlElement.position.set(
            this.panels.position.z - arrow.position.y,
            this.panels.position.x,
            -this.panels.position.y + arrow.position.z - this.middlePanel.options.dy.value / 2 - panelHTMLLabelsCorrection - 
              (this.options.panel.type === Panel.types.BEND ? 
                this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0)
          );
        } else {
          htmlElement.position.set(
            this.panels.position.x + this.middlePanel.size.depth / 2,
            this.panels.position.y + this.middlePanel.options.dy.value / 2 - panelHTMLLabelsCorrection -
              (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2),
            this.panels.position.z + arrow.position.y
          );
          htmlElement.optimalCameraDist = 3000;
          htmlElement.zoomFactor = 0.5;
          
          if (!options.displayHorizontally) {
            htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            htmlElement.position.z += 15;
            htmlElement.setDirection(
              htmlElement.position.clone().setY(htmlElement.position.y + this.middlePanel.options.dy.value / 2),
              htmlElement.position.clone().setY(htmlElement.position.y - this.middlePanel.options.dy.value / 2),
              false
            );
          }
        }
        layer.htmlLayer.add(htmlElement);

        // Create Dpp label
        if (this.options.panel.dpp.active) {
          const dppLabel = this.createDppLabel(this.middlePanel, options);
          if (this.is3DMode) {
            // dppLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            dppLabel.htmlElement.position.set(
              this.panels.position.x,
              this.panels.position.y + this.options.panel.dpp.value / 2 + 
              (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2),
              -this.panels.position.z - this.options.panel.width / 2 - LabelsPadding
            );
            dppLabel.htmlElement.optimalCameraDist = 3000;
            dppLabel.htmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              dppLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
              dppLabel.htmlElement.position.z -= 15;
              dppLabel.htmlElement.setDirection(
                dppLabel.htmlElement.position.clone().setY(
                  dppLabel.htmlElement.position.y + Math.max(25, this.DPP / 2)),
                dppLabel.htmlElement.position.clone().setY(
                  dppLabel.htmlElement.position.y - Math.max(25, this.DPP / 2)),
                false
              );
            }
          }
          layer.htmlLayer.add(dppLabel.htmlElement);
        }

        // Create depth label for panel
        let panelDepth;
        if (this.is3DMode) {
          if (this.middlePanel.type === Panel.types.BEND) {
            panelDepth = this.middlePanel.create3DDepthLabelForBend(options);
            layer.mesh.add(panelDepth.object);
          } else {
            panelDepth = this.middlePanel.create3DDepthLabel(options);
            panelDepth.object.rotation.z = Math.PI; // move on the other side to prevent overlap with DPP label
            this.middlePanel.mesh.add(panelDepth.object);
            panelDepth.htmlElement.align = HTMLEditableLabel.types.align.RIGHT;
            panelDepth.htmlElement.position.y = this.panels.position.y - panelDepth.htmlElement.position.x + panelHTMLLabelsCorrection;
            panelDepth.htmlElement.position.x = this.panels.position.x;
            panelDepth.htmlElement.position.z *= -1;
          }
          panelDepth.htmlElement.optimalCameraDist = 3000;
          panelDepth.htmlElement.zoomFactor = 0.5;
        } else {
          panelDepth = this.middlePanel.createDepthLabel();
          panelDepth.htmlElement.position.set(
            this.panels.position.z - panelDepth.path[1].y,
            this.panels.position.x,
            -this.panels.position.y + panelDepth.path[1].z - panelHTMLLabelsCorrection
          );
        }
        layer.htmlLayer.add(panelDepth.htmlElement);

        // Create width label
        const { line: widthLine, htmlElement: widthHtmlElement } = this.middlePanel.createWidthLabel(options);
        this.middlePanel.object.addMesh(widthLine);
        if (this.is2DMode) {
          widthHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
          widthHtmlElement.position.x -= (this.options.panel.type === Panel.types.BEND ? 
            this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0);
        } else {
          widthHtmlElement.position.set(
            this.panels.position.x,
            this.panels.position.y - panelHTMLLabelsCorrection + LabelsPadding + 
              (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2),
            this.panels.position.z
          );
          widthHtmlElement.optimalCameraDist = 3000;
          widthHtmlElement.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            widthHtmlElement.setDirection(
              widthHtmlElement.position.clone().setZ(widthHtmlElement.position.z + this.middlePanel.size.width / 2),
              widthHtmlElement.position.clone().setZ(widthHtmlElement.position.z - this.middlePanel.size.width / 2),
              false
            );
          }
          widthHtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.BOTTOM;
        }
        layer.htmlLayer.add(widthHtmlElement);

        // For not CIRCLE panel type
        if (this.middlePanel.options.type !== Panel.types.CIRCLE) {
          // Create Ly (height) label
          const { line: heightLine, htmlElement: heightHtmlElement } = this.middlePanel.createHeightLabel(options);
          if (this.is2DMode) {
            heightHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
            heightHtmlElement.position.x -= (this.options.panel.type === Panel.types.BEND ? 
              this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0);
          } else {
            heightHtmlElement.position.set(
              this.panels.position.x,
              this.panels.position.y,
              this.panels.position.z - this.middlePanel.size.width / 2 - LabelsPadding
            );
            heightHtmlElement.optimalCameraDist = 3000;
            heightHtmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              heightHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
              heightHtmlElement.position.z -= 15;
              heightHtmlElement.setDirection(
                heightHtmlElement.position.clone().setY(heightHtmlElement.position.y + this.middlePanel.size.height / 2),
                heightHtmlElement.position.clone().setY(heightHtmlElement.position.y - this.middlePanel.size.height / 2),
                false
                );
            } else {
              heightHtmlElement.align = HTMLEditableLabel.types.align.LEFT;
            }
          }
          this.middlePanel.object.addMesh(heightLine);
          layer.htmlLayer.add(heightHtmlElement);
        } else if (this.middlePanel.options.type === Panel.types.CIRCLE) {
          const { object: circleObject, htmlElement: circleHtmlElement } = this.screwGroup.createScrewCircle(options);
          if (this.is2DMode) {
            circleHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
          } else {
            circleHtmlElement.position.set(
              this.panels.position.x + this.middlePanel.size.depth / 2,
              this.panels.position.y - circleHtmlElement.position.x + 20,
              this.panels.position.z
            );
            circleHtmlElement.optimalCameraDist = 3000;
            circleHtmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              circleHtmlElement.setDirection(
                circleHtmlElement.position.clone().setZ(circleHtmlElement.position.z + this.middlePanel.size.width / 2),
                circleHtmlElement.position.clone().setZ(circleHtmlElement.position.z - this.middlePanel.size.width / 2),
                false
              );
            }
            circleHtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
          }
          this.middlePanel.object.addMesh(circleObject);
          layer.htmlLayer.add(circleHtmlElement);
        }

        if (this.middlePanel.options.type === Panel.types.SQUARE) {
          if (this.options.screw.count.n > 1) {
            // Create S1 label
            const { object: s1Object, htmlElement: s1HtmlElement } = this.middlePanel.createS1Label(
              this.screwGroup, 
              false, 
              LabelsPadding, 
              this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
              options
            );
            if (this.is2DMode) {
              s1HtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
            } else {
              s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
              s1HtmlElement.position.set(
                this.panels.position.x + this.middlePanel.size.depth / 2,
                this.panels.position.y - s1HtmlElement.position.z,
                this.panels.position.z
              );
              s1HtmlElement.optimalCameraDist = 3000;
              s1HtmlElement.zoomFactor = 0.5;
              if (!options.displayHorizontally) {
                s1HtmlElement.setDirection(
                  s1HtmlElement.position.clone().setZ(s1HtmlElement.position.z + this.screwGroup.options.padding.s1 / 2),
                  s1HtmlElement.position.clone().setZ(s1HtmlElement.position.z - this.screwGroup.options.padding.s1 / 2),
                  false
                );
              }
            }
            this.middlePanel.object.addMesh(s1Object);
            layer.htmlLayer.add(s1HtmlElement);
          }

          if (this.options.screw.count.m > 1) {
            // Create S2 label
            const { object: s2Object, htmlElement: s2HtmlElement } = this.middlePanel.createS2Label(
              this.screwGroup, 
              true, 
              this.middlePanel.size.width / 2 + LabelsPadding, 
              this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
              options
            );
            if (s2HtmlElement && s2Object) {
              if (this.is2DMode) {
                s2HtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
              } else {
                s2HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
                s2HtmlElement.position.set(
                  this.panels.position.x + this.middlePanel.size.depth / 2,
                  this.panels.position.y -
                    (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2) 
                    + this.middlePanel.options.dy.value + this.screwGroup.options.padding.s2 / 2 - panelHTMLLabelsCorrection,
                  this.panels.position.z + this.middlePanel.size.width / 2 + LabelsPadding
                );
                s2HtmlElement.optimalCameraDist = 3000;
                s2HtmlElement.zoomFactor = 0.5;
                if (!options.displayHorizontally) {
                  s2HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
                  s2HtmlElement.position.z += 15;
                  s2HtmlElement.setDirection(
                    s2HtmlElement.position.clone().setY(s2HtmlElement.position.y + this.screwGroup.options.padding.s2 / 2),
                    s2HtmlElement.position.clone().setY(s2HtmlElement.position.y - this.screwGroup.options.padding.s2 / 2),
                    false
                  );
                } else {
                  s2HtmlElement.align = HTMLEditableLabel.types.align.RIGHT;
                }
              }
              this.middlePanel.object.addMesh(s2Object);
              layer.htmlLayer.add(s2HtmlElement);
            }
          }
        } else if (this.middlePanel.options.type === Panel.types.BEND && Array.isArray(this.screwGroup.screwCoordinates[1])) {
          // Create S1 label
          const { object: s1Object, htmlElement: s1HtmlElement } = 
          this.middlePanel.createS1Label(
            this.screwGroup,
            false, 
            (this.screwGroup.screwCoordinates[1].length > 1 ? LabelsPadding : LabelsPadding /2), 
            this.middlePanel.size.height / 2 - (this.screwGroup.screwCoordinates[1].length > 1 ? this.middlePanel.options.dy.value : 0),
            options
          );
          if (s1HtmlElement && s1Object) {
            if (this.is2DMode) {
              s1HtmlElement.position.z -= this.panels.position.y;
            } else {
              if (this.screwGroup.screwCoordinates[1].length > 1) {
                s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
                s1HtmlElement.position.set(
                  this.panels.position.x + this.middlePanel.size.depth / 2,
                  this.panels.position.y - s1HtmlElement.position.z,
                  this.panels.position.z
                );
              } else {
                s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
                s1HtmlElement.position.set(
                  this.panels.position.x + s1HtmlElement.position.y,
                  this.panels.position.y - s1HtmlElement.position.z + LabelsPadding /2,
                  this.panels.position.z
                );
              }
              s1HtmlElement.optimalCameraDist = 3000;
              s1HtmlElement.zoomFactor = 0.5;
              if (!options.displayHorizontally) {
                s1HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
                s1HtmlElement.setDirection(
                  s1HtmlElement.position.clone().setZ(s1HtmlElement.position.y + this.screwGroup.options.padding.s1 / 2),
                  s1HtmlElement.position.clone().setZ(s1HtmlElement.position.y - this.screwGroup.options.padding.s1 / 2),
                  false
                );
              }
            }
            this.middlePanel.object.addMesh(s1Object);
            layer.htmlLayer.add(s1HtmlElement);
          }
        }

        if (this.is3DMode && this.options.panel.type === Panel.types.BEND) {
          // Create lyh label
          const lyh = this.middlePanel.createLyhLabel(this.options.object.dimensions);
          this.layer3D.addMesh(lyh.object);
          lyh.htmlElement.optimalCameraDist = 3000;
          lyh.htmlElement.zoomFactor = 0.5;
          this.layer3D.htmlLayer.add(lyh.htmlElement);
        }
        break;
      }

      case Panel.positions.VERTICAL_X_INVERT: {
        // base plate on the internal side
        // Create screw labels
        if (this.is2DMode) {
          screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
            htmlElem.position.x += this.panels.position.z;
            // htmlElem.position.y += this.panels.position.x;
            htmlElem.position.z -= this.panels.position.y + (this.options.panel.type === Panel.types.BEND ? 
              this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0);
          });

          // Create H1 label
          const h1Label = this.createH1SideViewLabel();
          layer.addMesh(h1Label.arrow);
          layer.htmlLayer.add(h1Label.htmlElement);
  
          // const { htmlElement: h2HtmlElement } = this.createH2SideViewLabel();
          // this.layer2D.htmlLayer.add(h2HtmlElement);
  
          // Create H2 label
          const { htmlElement: hrefHtmlElement } = this.createHref2DDashedLabel();
          layer.htmlLayer.add(hrefHtmlElement);
        }

        // Create label for Dy
        const { htmlElement, arrow } = this.screwGroup.createDyLabel(null, options);
        if (this.is2DMode) {
          htmlElement.position.set(
            this.panels.position.z - arrow.position.y,
            this.panels.position.x,
            -this.panels.position.y + arrow.position.z - this.middlePanel.options.dy.value / 2 - panelHTMLLabelsCorrection - 
            (this.options.panel.type === Panel.types.BEND ? 
              this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0)
          );
        } else {
          htmlElement.position.set(
            this.panels.position.x - this.middlePanel.size.depth / 2,
            this.panels.position.y + this.middlePanel.options.dy.value / 2 - panelHTMLLabelsCorrection -
              (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2),
            this.panels.position.z - arrow.position.y
          );
          if (!options.displayHorizontally) {
            htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            htmlElement.position.z -= 15;
            htmlElement.setDirection(
              htmlElement.position.clone().setY(htmlElement.position.y + this.middlePanel.options.dy.value / 2),
              htmlElement.position.clone().setY(htmlElement.position.y - this.middlePanel.options.dy.value / 2),
              false
            );
          } else {
            htmlElement.align = HTMLEditableLabel.types.align.RIGHT;
          }
          htmlElement.optimalCameraDist = 3000;
          htmlElement.zoomFactor = 0.5;
        }
        layer.htmlLayer.add(htmlElement);

        // Create Dpp label
        if (this.options.panel.dpp.active) {
          const dppLabel = this.createDppLabel(this.middlePanel, options);
          if (this.is3DMode) {
            // dppLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            dppLabel.htmlElement.position.set(
              this.panels.position.x,
              this.panels.position.y + this.options.panel.dpp.value / 2 + 
              (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2),
              this.panels.position.z + this.options.panel.width / 2 + LabelsPadding
            );
            dppLabel.htmlElement.optimalCameraDist = 3000;
            dppLabel.htmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              dppLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
              dppLabel.htmlElement.position.z += 15;
              dppLabel.htmlElement.setDirection(
                dppLabel.htmlElement.position.clone().setY(
                  dppLabel.htmlElement.position.y + Math.max(25, this.DPP / 2)),
                dppLabel.htmlElement.position.clone().setY(
                  dppLabel.htmlElement.position.y - Math.max(25, this.DPP / 2)),
                false
              );
            }
          }
          layer.htmlLayer.add(dppLabel.htmlElement);
        }

        // Create depth label for panel
        let panelDepth;
        if (this.is3DMode) {
          if (this.middlePanel.type === Panel.types.BEND) {
            panelDepth = this.middlePanel.create3DDepthLabelForBend(options);
            panelDepth.object.rotation.y = Math.PI;
            panelDepth.object.position.x = panelDepth.htmlElement.position.x * 2;
            layer.addMesh(panelDepth.object);
            panelDepth.htmlElement.position.z *= -1;
            panelDepth.htmlElement.align = HTMLEditableLabel.types.align.LEFT;
            layer.mesh.add(panelDepth.object);
          } else {
            panelDepth = this.middlePanel.create3DDepthLabel(options);
            panelDepth.object.rotation.z = Math.PI; // move on the other side to prevent overlap with DPP label
            this.middlePanel.mesh.add(panelDepth.object);
            panelDepth.htmlElement.align = HTMLEditableLabel.types.align.RIGHT;
            panelDepth.htmlElement.position.y = this.panels.position.y - panelDepth.htmlElement.position.x + panelHTMLLabelsCorrection;
            panelDepth.htmlElement.position.x = this.panels.position.x;
          }
          panelDepth.htmlElement.optimalCameraDist = 3000;
          panelDepth.htmlElement.zoomFactor = 0.5;
        } else {
          panelDepth = this.middlePanel.createDepthLabel();
          panelDepth.htmlElement.position.set(
            this.panels.position.z - panelDepth.path[1].y,
            this.panels.position.x,
            -this.panels.position.y + panelDepth.path[1].z - panelHTMLLabelsCorrection
          );
        }
        layer.htmlLayer.add(panelDepth.htmlElement);

        // Create width label
        const { line: widthLine, htmlElement: widthHtmlElement } = this.middlePanel.createWidthLabel(options);
        if (this.is2DMode) {
          widthHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
          widthHtmlElement.position.x -= (this.options.panel.type === Panel.types.BEND ? 
            this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0);
        } else {
          widthHtmlElement.position.set(
            this.panels.position.x,
            this.panels.position.y - panelHTMLLabelsCorrection + LabelsPadding + 
              (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2),
            this.panels.position.z
          );
          widthHtmlElement.optimalCameraDist = 3000;
          widthHtmlElement.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            widthHtmlElement.setDirection(
              widthHtmlElement.position.clone().setZ(widthHtmlElement.position.z + this.middlePanel.size.width / 2),
              widthHtmlElement.position.clone().setZ(widthHtmlElement.position.z - this.middlePanel.size.width / 2),
              false
            );
          }
          widthHtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.BOTTOM;
        }
        this.middlePanel.object.addMesh(widthLine);
        layer.htmlLayer.add(widthHtmlElement);

        // For not CIRCLE panel type
        if (this.middlePanel.options.type !== Panel.types.CIRCLE) {
          // Create height label
          const { line: heightLine, htmlElement: heightHtmlElement } = this.middlePanel.createHeightLabel(options);
          if (this.is2DMode) {
            heightHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
            heightHtmlElement.position.x -= (this.options.panel.type === Panel.types.BEND ? 
              this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0);
          } else {
            heightHtmlElement.position.set(
              this.panels.position.x,
              this.panels.position.y,
              this.panels.position.z + this.middlePanel.size.width / 2 + LabelsPadding
            );
            heightHtmlElement.optimalCameraDist = 3000;
            heightHtmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              heightHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
              heightHtmlElement.position.z += 15;
              heightHtmlElement.setDirection(
                heightHtmlElement.position.clone().setY(heightHtmlElement.position.y + this.middlePanel.size.height / 2),
                heightHtmlElement.position.clone().setY(heightHtmlElement.position.y - this.middlePanel.size.height / 2),
                false
              );
            } else {
              heightHtmlElement.align = HTMLEditableLabel.types.align.LEFT;
            }
          }
          this.middlePanel.object.addMesh(heightLine);
          layer.htmlLayer.add(heightHtmlElement);
        } else if (this.middlePanel.options.type === Panel.types.CIRCLE) {
          const { object: circleObject, htmlElement: circleHtmlElement } = this.screwGroup.createScrewCircle(options);
          if (this.is2DMode) {
            circleHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
          } else {
            circleHtmlElement.position.set(
              this.panels.position.x - this.middlePanel.size.depth / 2,
              this.panels.position.y - circleHtmlElement.position.x + 20,
              this.panels.position.z
            );
            circleHtmlElement.optimalCameraDist = 3000;
            circleHtmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              circleHtmlElement.setDirection(
                circleHtmlElement.position.clone().setZ(circleHtmlElement.position.z + this.middlePanel.size.width / 2),
                circleHtmlElement.position.clone().setZ(circleHtmlElement.position.z - this.middlePanel.size.width / 2),
                false
              );
            }
            circleHtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
          }
          this.middlePanel.object.addMesh(circleObject);
          layer.htmlLayer.add(circleHtmlElement);
        }

        if (this.middlePanel.options.type === Panel.types.SQUARE) {
          if (this.options.screw.count.n > 1) {
            // Create S1 label
            const { object: s1Object, htmlElement: s1HtmlElement } = this.middlePanel.createS1Label(
              this.screwGroup, 
              false, 
              LabelsPadding, 
              this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
              options
            );
            if (this.is2DMode) {
              s1HtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
            } else {
              s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
              s1HtmlElement.position.set(
                this.panels.position.x - this.middlePanel.size.depth / 2,
                this.panels.position.y - s1HtmlElement.position.z,
                this.panels.position.z
              );
              s1HtmlElement.optimalCameraDist = 3000;
              s1HtmlElement.zoomFactor = 0.5;
              if (!options.displayHorizontally) {
                s1HtmlElement.setDirection(
                  s1HtmlElement.position.clone().setZ(s1HtmlElement.position.z + this.screwGroup.options.padding.s1 / 2),
                  s1HtmlElement.position.clone().setZ(s1HtmlElement.position.z - this.screwGroup.options.padding.s1 / 2),
                  false
                );
              }
            }
            this.middlePanel.object.addMesh(s1Object);
            layer.htmlLayer.add(s1HtmlElement);
          }

          if (this.options.screw.count.m > 1) {
            // Create S2 label
            const { object: s2Object, htmlElement: s2HtmlElement } = this.middlePanel.createS2Label(
              this.screwGroup, 
              true, 
              this.middlePanel.size.width / 2 + LabelsPadding, 
              this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
              options
            );
            if (s2HtmlElement && s2Object) {
              if (this.is2DMode) {
                s2HtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
              } else {
                s2HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
                s2HtmlElement.position.set(
                  this.panels.position.x - this.middlePanel.size.depth / 2,
                  this.panels.position.y -
                    (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2) 
                    + this.middlePanel.options.dy.value + this.screwGroup.options.padding.s2 / 2 - panelHTMLLabelsCorrection,
                  this.panels.position.z - this.middlePanel.size.width / 2 - LabelsPadding
                );
                s2HtmlElement.optimalCameraDist = 3000;
                s2HtmlElement.zoomFactor = 0.5;
                
                if (!options.displayHorizontally) {
                  s2HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
                  s2HtmlElement.position.z -= 15;
                  s2HtmlElement.setDirection(
                    htmlElement.position.clone().setY(s2HtmlElement.position.y + this.screwGroup.options.padding.s2 / 2),
                    htmlElement.position.clone().setY(s2HtmlElement.position.y - this.screwGroup.options.padding.s2 / 2),
                    false
                  );
                } else {
                  s2HtmlElement.align = HTMLEditableLabel.types.align.RIGHT;
                }
              }
              this.middlePanel.object.addMesh(s2Object);
              layer.htmlLayer.add(s2HtmlElement);
            }
          }
        } else if (this.middlePanel.options.type === Panel.types.BEND && Array.isArray(this.screwGroup.screwCoordinates[1])) {
          // Create S1 label
          const { object: s1Object, htmlElement: s1HtmlElement } = 
            this.middlePanel.createS1Label(
              this.screwGroup, 
              false, 
              (this.screwGroup.screwCoordinates[1].length > 1 ? LabelsPadding : LabelsPadding /2), 
              this.middlePanel.size.height / 2 - (this.screwGroup.screwCoordinates[1].length > 1 ? this.middlePanel.options.dy.value : 0),
              options
            );
          if (s1HtmlElement && s1Object) {
            if (this.is2DMode) {
              s1HtmlElement.position.z -= this.panels.position.y;
            } else {
              if (this.screwGroup.screwCoordinates[1].length > 1) {
                s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
                s1HtmlElement.position.set(
                  this.panels.position.x - this.middlePanel.size.depth / 2,
                  this.panels.position.y - s1HtmlElement.position.z,
                  this.panels.position.z
                );
              } else {
                s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
                s1HtmlElement.position.set(
                  this.panels.position.x - s1HtmlElement.position.y,
                  this.panels.position.y - s1HtmlElement.position.z + LabelsPadding /2,
                  this.panels.position.z
                );
              }
              s1HtmlElement.optimalCameraDist = 3000;
              s1HtmlElement.zoomFactor = 0.5;
              if (!options.displayHorizontally) {
                s1HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
                s1HtmlElement.setDirection(
                  s1HtmlElement.position.clone().setZ(s1HtmlElement.position.y + this.screwGroup.options.padding.s1 / 2),
                  s1HtmlElement.position.clone().setZ(s1HtmlElement.position.y - this.screwGroup.options.padding.s1 / 2),
                  false
                );
              }
            }
            this.middlePanel.object.addMesh(s1Object);
            layer.htmlLayer.add(s1HtmlElement);
          }
        }

        if (this.is3DMode && this.options.panel.type === Panel.types.BEND) {
          // Create lyh label
          const lyh = this.middlePanel.createLyhLabel(this.options.object.dimensions);
          lyh.object.rotation.x = Math.PI;
          lyh.object.position.y = lyh.htmlElement.position.y * 2;
          lyh.object.position.x = - this.middlePanel.size.depth;
          layer.addMesh(lyh.object);
          lyh.htmlElement.position.z *= -1;
          lyh.htmlElement.position.x -= this.middlePanel.size.depth;
          
          if (!options.displayHorizontally) {
            lyh.htmlElement.setDirection(
              lyh.htmlElement.position.clone().setX(lyh.htmlElement.position.x + this.middlePanel.size.topHeight / 2),
              lyh.htmlElement.position.clone().setX(lyh.htmlElement.position.x - this.middlePanel.size.topHeight / 2),
              false
            );
          }
          lyh.htmlElement.optimalCameraDist = 3000;
          lyh.htmlElement.zoomFactor = 0.5;
          layer.htmlLayer.add(lyh.htmlElement);
        }

        break;
      }

      default: {
        break;
      }
    }

    this.createShiftLabels(layer, options);

  }

  setOrientation() {
    super.setOrientation();

    if (this.is2DMode) {
      switch (this.options.panel.position) {
        case Panel.positions.HORIZONTAL_Y: {
          this.layer2D.mesh.rotation.x = 0;
          this.layer2D.mesh.rotation.y = Math.PI / -2;
          this.layer2D.mesh.rotation.z = 0;

          break;
        }
        case Panel.positions.VERTICAL_X: {
          this.layer2D.mesh.rotation.x = 0;
          this.layer2D.mesh.rotation.y = Math.PI / -2;
          this.layer2D.mesh.rotation.z = Math.PI / 2;

          break;
        }
        case Panel.positions.VERTICAL_X_INVERT: {
          this.layer2D.mesh.rotation.x = 0;
          this.layer2D.mesh.rotation.y = Math.PI / 2;
          this.layer2D.mesh.rotation.z = Math.PI / -2;

          break;
        }
        default: {
          break;
        }
      }
    }
  }

  shiftBarrier() {
    if (this.options.beam.shift) {
      switch (this.options.panel.position) {
        case Panel.positions.HORIZONTAL_Y: {
          this.barrier.mesh.position.z -= this.options.beam.shift.x;
          this.barrier.mesh.position.x -= this.options.beam.shift.y;
          break;
        }
        case Panel.positions.VERTICAL_X: {
          this.barrier.mesh.position.z -= this.options.beam.shift.x;
          this.barrier.mesh.position.y += this.options.beam.shift.y;
          break;
        }
        case Panel.positions.VERTICAL_X_INVERT: {
          this.barrier.mesh.position.z += this.options.beam.shift.x;
          this.barrier.mesh.position.y += this.options.beam.shift.y;
          break;
        }
        default:
          break;
      }
    }
  }

  createShiftLabels(layer, options) {
    var shiftLabels = null;
    switch (this.options.panel.position) {
      case Panel.positions.HORIZONTAL_Y: {
        shiftLabels = this.barrier.createShiftLabels(
          this.dispatchOptionUpdate, 
          this.options.beam.shift, 
          new THREE.Vector3(this.panels.position.z, -this.panels.position.x, this.panels.position.y),
          options
        );

        if (shiftLabels) {
          if (shiftLabels.marker) {
            shiftLabels.marker.position.set(
              this.panels.position.x, 
              this.panels.position.y + this.middlePanel.size.depth / 2, 
              this.panels.position.z
            );
            layer.addObject(shiftLabels.marker);
          }
          if (shiftLabels.xLabel) {
            shiftLabels.xLabel.object.rotation.set(0, 1.57, 1.57, "ZXY");
            shiftLabels.xLabel.object.position.y += this.middlePanel.size.depth / 2;
            if (options && options.is3DMode) {
              shiftLabels.xLabel.HTMLObject.verticalAlign = HTMLEditableLabel.types.align.CENTER;
              shiftLabels.xLabel.HTMLObject.optimalCameraDist = 3000;
              shiftLabels.xLabel.HTMLObject.zoomFactor = 0.5;
              shiftLabels.xLabel.HTMLObject.position.set(
                shiftLabels.xLabel.HTMLObject.position.z + (this.options.beam.shift.y < 0 ? -20 : 20),
                shiftLabels.xLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
                -shiftLabels.xLabel.HTMLObject.position.x,
              );
              if (!options.displayHorizontally) {
                shiftLabels.xLabel.HTMLObject.setDirection(
                  shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                    shiftLabels.xLabel.HTMLObject.position.z + Math.max(25, this.options.beam.shift.x / 2)
                  ), 
                  shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                    shiftLabels.xLabel.HTMLObject.position.z - Math.max(25, this.options.beam.shift.x / 2)
                  ),
                  false
                );
              }
            }
            layer.addMesh(shiftLabels.xLabel.object);
            layer.htmlLayer.add(shiftLabels.xLabel.HTMLObject);
          }
          if (shiftLabels.yLabel) {
            shiftLabels.yLabel.object.rotation.set(0, 1.57, 1.57, "ZXY");
            shiftLabels.yLabel.object.position.y += this.middlePanel.size.depth / 2;
            if (options && options.is3DMode) {
              shiftLabels.yLabel.HTMLObject.align = HTMLEditableLabel.types.align.CENTER;
              shiftLabels.yLabel.HTMLObject.optimalCameraDist = 3000;
              shiftLabels.yLabel.HTMLObject.zoomFactor = 0.5;
              shiftLabels.yLabel.HTMLObject.position.set(
                shiftLabels.yLabel.HTMLObject.position.z,
                shiftLabels.yLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
                -shiftLabels.yLabel.HTMLObject.position.x + (this.options.beam.shift.x < 0 ? -20 : 20),
              );
              if (!options.displayHorizontally) {
                shiftLabels.yLabel.HTMLObject.setDirection(
                  shiftLabels.yLabel.HTMLObject.position.clone().setX(
                    shiftLabels.yLabel.HTMLObject.position.x + Math.max(25, this.options.beam.shift.y / 2)
                  ), 
                  shiftLabels.yLabel.HTMLObject.position.clone().setX(
                    shiftLabels.yLabel.HTMLObject.position.x - Math.max(25, this.options.beam.shift.y / 2)
                  ),
                  false
                );
              }
            }
            layer.addMesh(shiftLabels.yLabel.object);
            layer.htmlLayer.add(shiftLabels.yLabel.HTMLObject);
          }
        }
        break;
      }
      case Panel.positions.VERTICAL_X: {
        shiftLabels = this.barrier.createShiftLabels(
          this.dispatchOptionUpdate, 
          this.options.beam.shift, 
          new THREE.Vector3(this.panels.position.z, this.panels.position.y, this.panels.position.x),
          options
        );
        
        if (shiftLabels) {
          if (shiftLabels.marker) {
            shiftLabels.marker.rotation.z = Math.PI / 2;
            shiftLabels.marker.position.set(
              this.panels.position.x + this.middlePanel.size.depth / 2, 
              this.panels.position.y, 
              this.panels.position.z);
            layer.addObject(shiftLabels.marker);
          }
          if (shiftLabels.xLabel) {
            shiftLabels.xLabel.object.rotation.y = Math.PI / 2;
            if (options && options.is3DMode) {
              shiftLabels.xLabel.object.position.x += this.middlePanel.size.depth / 2;
              shiftLabels.xLabel.HTMLObject.optimalCameraDist = 3000;
              shiftLabels.xLabel.HTMLObject.zoomFactor = 0.5;
              shiftLabels.xLabel.HTMLObject.verticalAlign = this.options.beam.shift.y < 0 ? 
                HTMLEditableLabel.types.verticalAlign.BOTTOM : HTMLEditableLabel.types.verticalAlign.TOP;
              shiftLabels.xLabel.HTMLObject.position.set(
                shiftLabels.xLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
                -shiftLabels.xLabel.HTMLObject.position.z, // + (this.options.beam.shift.y < 0 ? 20 : -20),
                -shiftLabels.xLabel.HTMLObject.position.x,
              );
              if (!options.displayHorizontally) {
                shiftLabels.xLabel.HTMLObject.setDirection(
                  shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                    shiftLabels.xLabel.HTMLObject.position.z + Math.max(25, this.options.beam.shift.x / 2)
                  ), 
                  shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                    shiftLabels.xLabel.HTMLObject.position.z - Math.max(25, this.options.beam.shift.x / 2)
                  ),
                  false
                );
              }
            }
            layer.addMesh(shiftLabels.xLabel.object);
            layer.htmlLayer.add(shiftLabels.xLabel.HTMLObject);
          }
          if (shiftLabels.yLabel) {
            shiftLabels.yLabel.object.rotation.y = Math.PI / 2;
            if (options && options.is3DMode) {
              shiftLabels.yLabel.object.position.x += this.middlePanel.size.depth / 2;
              shiftLabels.yLabel.HTMLObject.optimalCameraDist = 3000;
              shiftLabels.yLabel.HTMLObject.zoomFactor = 0.5;
              shiftLabels.yLabel.HTMLObject.align = HTMLLabel.types.align.CENTER;
              shiftLabels.yLabel.HTMLObject.verticalAlign = HTMLEditableLabel.types.align.CENTER;
              shiftLabels.yLabel.HTMLObject.position.set(
                shiftLabels.yLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
                -shiftLabels.yLabel.HTMLObject.position.z,
                -shiftLabels.yLabel.HTMLObject.position.x + (this.options.beam.shift.x < 0 ? -20 : 20),
              );
              if (!options.displayHorizontally) {
                shiftLabels.yLabel.HTMLObject.setDirection(
                  shiftLabels.yLabel.HTMLObject.position.clone().setY(
                    shiftLabels.yLabel.HTMLObject.position.y + Math.max(25, this.options.beam.shift.y / 2)
                  ), 
                  shiftLabels.yLabel.HTMLObject.position.clone().setY(
                    shiftLabels.yLabel.HTMLObject.position.y - Math.max(25, this.options.beam.shift.y / 2)
                  ),
                  false
                );
              }
            }
            layer.addMesh(shiftLabels.yLabel.object);
            layer.htmlLayer.add(shiftLabels.yLabel.HTMLObject);
          }
        }
        break;
      }
      case Panel.positions.VERTICAL_X_INVERT: {
        shiftLabels = this.barrier.createShiftLabels(
          this.dispatchOptionUpdate, 
          this.options.beam.shift, 
          new THREE.Vector3(this.panels.position.z, this.panels.position.y, this.panels.position.x),
          options
        );
        
        if (shiftLabels) {
          if (shiftLabels.marker) {
            shiftLabels.marker.rotation.z = Math.PI / 2;
            shiftLabels.marker.position.set(
              this.panels.position.x - this.middlePanel.size.depth / 2, 
              this.panels.position.y, 
              this.panels.position.z
            );
            layer.addObject(shiftLabels.marker);
          }
          if (shiftLabels.xLabel) {
            shiftLabels.xLabel.object.rotation.y = -Math.PI / 2;
            if (options && options.is3DMode) {
              shiftLabels.xLabel.object.position.x = 
                this.ground.size.finish.height + this.panels.position.x - this.middlePanel.size.depth;
              shiftLabels.xLabel.HTMLObject.optimalCameraDist = 3000;
              shiftLabels.xLabel.HTMLObject.zoomFactor = 0.5;
              shiftLabels.xLabel.HTMLObject.verticalAlign = this.options.beam.shift.y < 0 ? 
                HTMLEditableLabel.types.verticalAlign.BOTTOM : HTMLEditableLabel.types.verticalAlign.TOP;
              shiftLabels.xLabel.HTMLObject.position.set(
                shiftLabels.xLabel.HTMLObject.position.y - this.middlePanel.size.depth / 2,
                -shiftLabels.xLabel.HTMLObject.position.z, // + (this.options.beam.shift.y < 0 ? 20 : -20),
                shiftLabels.xLabel.HTMLObject.position.x,
              );
              if (!options.displayHorizontally) {
                shiftLabels.xLabel.HTMLObject.setDirection(
                  shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                    shiftLabels.xLabel.HTMLObject.position.z + Math.max(25, this.options.beam.shift.x / 2)
                  ), 
                  shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                    shiftLabels.xLabel.HTMLObject.position.z - Math.max(25, this.options.beam.shift.x / 2)
                  ),
                  false
                );
              }
            }
            layer.addMesh(shiftLabels.xLabel.object);
            layer.htmlLayer.add(shiftLabels.xLabel.HTMLObject);
          }
          if (shiftLabels.yLabel) {
            // shiftLabels.yLabel.object.rotation.set(0, 1.57, 1.57, "ZXY");
            shiftLabels.yLabel.object.rotation.y = -Math.PI / 2;
            if (options && options.is3DMode) {
              shiftLabels.yLabel.object.position.x = 
                this.ground.size.finish.height + this.panels.position.x - this.middlePanel.size.depth;
              shiftLabels.yLabel.HTMLObject.optimalCameraDist = 3000;
              shiftLabels.yLabel.HTMLObject.zoomFactor = 0.5;
              shiftLabels.yLabel.HTMLObject.align = HTMLLabel.types.align.CENTER;
              shiftLabels.yLabel.HTMLObject.verticalAlign = HTMLEditableLabel.types.align.CENTER;
              shiftLabels.yLabel.HTMLObject.position.set(
                shiftLabels.yLabel.HTMLObject.position.y - this.middlePanel.size.depth / 2,
                -shiftLabels.yLabel.HTMLObject.position.z,
                shiftLabels.yLabel.HTMLObject.position.x + (this.options.beam.shift.x < 0 ? 20 : -20),
              );
              if (!options.displayHorizontally) {
                shiftLabels.yLabel.HTMLObject.setDirection(
                  shiftLabels.yLabel.HTMLObject.position.clone().setY(
                    shiftLabels.yLabel.HTMLObject.position.y + Math.max(25, this.options.beam.shift.y / 2)
                  ), 
                  shiftLabels.yLabel.HTMLObject.position.clone().setY(
                    shiftLabels.yLabel.HTMLObject.position.y - Math.max(25, this.options.beam.shift.y / 2)
                  ),
                  false
                );
              }
            }
            layer.addMesh(shiftLabels.yLabel.object);
            layer.htmlLayer.add(shiftLabels.yLabel.HTMLObject);
          }
        }
        break;
      }
      default:
        break;
    }
    
  }

  get DPP() {
    return this.options.panel.dpp.active ? this.options.panel.dpp.value : 0;
  }

  createDppLabel(parent, options) {
    const panelHeight = this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width : this.options.panel.height;
    const arrow = new Arrow({
      size: -this.options.panel.dpp.value,
      position: {
        x: 0,
        y: -this.options.panel.width / 2 - LabelsPadding,
        z: -panelHeight / 2
      },
      direction: Arrow.types.direction.Z,
      disableDepthTest: true
    });

    parent.object.addObject(arrow);
    
    if (this.options.panel.type === Panel.types.CIRCLE) {
      const bottomLine = new Line({
        from: {
          x: 0,
          y: 0,
          z: this.panels.position.z - this.options.panel.width / 2
        },
        to: {
          x: 0,
          y: -this.options.panel.width / 2 - LabelsPadding,
          z: this.panels.position.z - this.options.panel.width / 2
        },
        disableDepthTest: true
      });
      parent.object.addObject(bottomLine);
    }

    const htmlElement = new HTMLEditableLabel({
      text: ((!options || !options.is3DMode || options.showLabels) ? 'Dpp = ' : '') + 
      `${unitTransform(+this.options.panel.dpp.value).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.options.panel.dpp.value).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        panel: {
          dpp: {
            value: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      this.panels.position.z + this.options.panel.width / 2 + LabelsPadding,
      this.panels.position.x,
      -this.panels.position.y - panelHeight / 2 - this.options.panel.dpp.value / 2
    );

    return {
      arrow,
      htmlElement
    };
  }

  createWLabel() {
    const { width } = this.options.ground.pedestal;
    const value = this.options.ground.pedestal.pedestal.height;

    const position = {
      x: this.ground.position.x + this.ground.size.ground.width / 2 - this.ground.size.pedestal.height,
      y: 0,
      z: width / 2 + LabelsPadding
    };

    const object = new THREE.Object3D();
    const arrow = new Arrow({
      size: value,
      position,
      direction: Arrow.types.direction.X,
      disableDepthTest: true
    });
    
    object.add(arrow.mesh);

    const topLine = new Line({
      from: {
        x: position.x,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x,
        y: position.y,
        z: position.z - LabelsPadding
      }
    });
    object.add(topLine.mesh);

    const bottomLine = new Line({
      from: {
        x: position.x + value,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x + value,
        y: position.y,
        z: position.z - LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(bottomLine.mesh);
    this.layer2D.addMesh(object);

    const htmlElement = new HTMLEditableLabel({
      text: `W: ${unitTransform(+value).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+value).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.RIGHT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((val, didChange) => {
      this.dispatchOptionUpdate({
        ground: {
          pedestal: {
            pedestal: {
              height: unitTransform(+val, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      -position.z,
      position.y,
      position.x + value / 2
    );

    return {
      arrow: object,
      htmlElement
    };
  }

  createH1Label() {
    // const { width } = this.options.ground.pedestal;

    const position = {
      x: this.ground.position.x + this.ground.size.ground.width / 2 - LabelsPadding,
      y: 0,
      z: -this.options.ground.pedestal.width / 2 + LabelsPadding
    };

    const path = [{ ...position }]; // eslint-disable-line

    position.x -= 100;
    position.z -= 100;
    path.push({ ...position });

    position.z -= 100;
    path.push({ ...position });

    const line = new Line({ path, disableDepthTest: true });
    this.layer2D.addObject(line);

    const htmlElement = new HTMLEditableLabel({
      text: `H1 = ${unitTransform(+this.options.ground.pedestal.pedestal.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.options.ground.pedestal.pedestal.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        ground: {
          pedestal: {
            pedestal: {
              depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      -path[1].z,
      path[1].y,
      path[1].x
    );

    return {
      htmlElement,
      path,
      line
    };
  }

  createH1SideViewLabel(options) {
    const { width } = this.options.ground.pedestal;
    const value = this.options.ground.pedestal.pedestal.depth;

    const position = {
      x: 0,
      y: this.ground.position.y + this.options.ground.pedestal.ground.depth,
      z: width / 2 + LabelsPadding
    };

    const object = new THREE.Object3D();
    const arrow = new Arrow({
      size: value,
      position,
      direction: Arrow.types.direction.Y,
      disableDepthTest: true
    });
    object.add(arrow.mesh);

    const topLine = new Line({
      from: {
        x: position.x,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x,
        y: position.y,
        z: position.z - LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(topLine.mesh);

    const bottomLine = new Line({
      from: {
        x: position.x,
        y: position.y + value,
        z: position.z
      },
      to: {
        x: position.x,
        y: position.y + value,
        z: position.z - LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(bottomLine.mesh);

    const htmlElement = new HTMLEditableLabel({
      text: (!options || !options.is3DMode || options.showLabels ? 'H1 = ' : '') + 
        `${unitTransform(+value).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+value).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.LEFT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((val, didChange) => {
      this.dispatchOptionUpdate({
        ground: {
          pedestal: {
            pedestal: {
              depth: unitTransform(+val, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      position.z,
      position.x,
      -position.y - value / 2
    );

    return {
      arrow: object,
      htmlElement
    };
  }

  createH2SideViewLabel(options) {
    const { width } = this.options.ground.pedestal;
    const value = this.options.ground.pedestal.ground.depth;

    const position = {
      x: 0,
      y: this.ground.position.y,
      z: width / 2 + LabelsPadding
    };

    const object = new THREE.Object3D();
    const arrow = new Arrow({
      size: value,
      position,
      direction: Arrow.types.direction.Y,
      disableDepthTest: true
    });
    object.add(arrow.mesh);

    const topLine = new Line({
      from: {
        x: position.x,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x,
        y: position.y,
        z: position.z - LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(topLine.mesh);

    const bottomLine = new Line({
      from: {
        x: position.x,
        y: position.y + value,
        z: position.z
      },
      to: {
        x: position.x,
        y: position.y + value,
        z: position.z - LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(bottomLine.mesh);

    const htmlElement = new HTMLEditableLabel({
      text: (!options || !options.is3DMode || options.showLabels ? 'H2 = ' : '') + 
        `${unitTransform(+value).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+value).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.LEFT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((val, didChange) => {
      this.dispatchOptionUpdate({
        ground: {
          pedestal: {
            ground: {
              depth: unitTransform(+val, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      position.z,
      position.x,
      -position.y - value / 2
    );

    return {
      arrow: object,
      htmlElement
    };
  }

  createHref2DDashedLabel() {
    const { width } = this.options.ground.pedestal;

    const position = {
      x: 0,
      y: this.ground.position.x + this.ground.size.ground.depth + this.ground.size.finish.depth,
      z: width / 2 + LabelsPadding
    };

    const path = [{ ...position }]; // eslint-disable-line

    position.z -= width + LabelsPadding * 2;
    path.push({ ...position });

    const line = new Line({ path, disableDepthTest: true, color: 0xb7a768, dashed: true });
    this.layer2D.addObject(line);

    const htmlElement = new HTMLLabel({
      text: 'Href',
      verticalAlign: HTMLLabel.types.verticalAlign.BOTTOM,
      align: HTMLEditableLabel.types.align.RIGHT,
    });
    htmlElement.position.set(
      -width / 2,//-path[0].z,
      path[0].x,
      -path[0].y
    );

    return {
      htmlElement,
      path,
      line
    };
  }

}



// WEBPACK FOOTER //
// ./src/object-view/generators/safety-barrier/objects/pedestal.js